import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Input,
  Button,
  useToast,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Spinner,
  Icon,
  HStack
} from '@chakra-ui/react';
import { LockIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FaEye, FaClock } from 'react-icons/fa';
import PitchDeck from './PitchDeck';
import { supabase } from '../supabaseClient';

const SecurePitchDeck = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  const trackPageView = useCallback(async (email, page) => {
    try {
      await supabase
        .from('pitch_deck_analytics')
        .insert([
          {
            email: email,
            page: page,
            timestamp: new Date().toISOString(),
            session_id: sessionStorage.getItem('pitch_deck_session_id') || generateSessionId()
          }
        ]);
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }, []);

  // Check if user is already authenticated
  useEffect(() => {
    const isAuthenticated = sessionStorage.getItem('pitch_deck_authenticated');
    const email = sessionStorage.getItem('pitch_deck_user_email');
    if (isAuthenticated === 'true' && email) {
      setAuthenticated(true);
      setUserEmail(email);
      // Track page view
      trackPageView(email, 'pitch-deck-entry');
    }
  }, [trackPageView]);

  const generateSessionId = () => {
    const sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    sessionStorage.setItem('pitch_deck_session_id', sessionId);
    return sessionId;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast({
        title: 'Password required',
        description: 'Please enter the access password',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      // Check password against database
      const { data, error } = await supabase
        .from('password_requests')
        .select('email, expires_at, used_at')
        .eq('password', password)
        .gte('expires_at', new Date().toISOString())
        .order('requested_at', { ascending: false })
        .limit(1);

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        const request = data[0];
        
        // Mark password as used
        await supabase
          .from('password_requests')
          .update({ used_at: new Date().toISOString() })
          .eq('password', password)
          .eq('email', request.email);

        // Log successful access
        await supabase
          .from('pitch_deck_access_log')
          .insert([
            {
              email: request.email,
              accessed_at: new Date().toISOString(),
              ip_address: 'unknown', // Could be enhanced with actual IP
              user_agent: navigator.userAgent
            }
          ]);

        // Set authentication
        sessionStorage.setItem('pitch_deck_authenticated', 'true');
        sessionStorage.setItem('pitch_deck_user_email', request.email);
        setAuthenticated(true);
        setUserEmail(request.email);
        
        // Track initial access
        trackPageView(request.email, 'pitch-deck-entry');

        toast({
          title: 'Access granted',
          description: 'Welcome to the exclusive presentation!',
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Access denied',
          description: 'Invalid or expired password. Please request a new one.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      toast({
        title: 'Error',
        description: 'There was an error verifying your password. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPassword = () => {
    navigate('/Product-offering');
  };

  // Show pitch deck if authenticated
  if (authenticated) {
    return <PitchDeck userEmail={userEmail} trackPageView={trackPageView} />;
  }

  // Show password entry form
  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="lg">
        <VStack spacing={8} align="stretch">
          
          {/* Header with Logo */}
          <VStack spacing={6} textAlign="center">
            <Box 
              bg="white"
              px={8}
              py={4}
              borderRadius="xl"
              shadow="xl"
              display="inline-block"
              border="3px solid"
              borderColor="blue.100"
            >
              <Text 
                fontSize="2xl" 
                fontWeight="800" 
                color="#0066cc"
                letterSpacing="tight"
              >
                AtHomeBloodDraw.com
              </Text>
            </Box>
            
            <VStack spacing={3}>
              <Box p={4} bg="blue.100" borderRadius="full">
                <LockIcon boxSize={8} color="blue.600" />
              </Box>
              <Heading size="xl" color="#0066cc">
                Secure Access Required
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="md">
                Enter your access password to view the exclusive product offering presentation
              </Text>
              
              <HStack spacing={4} pt={2}>
                <HStack spacing={2}>
                  <Icon as={FaEye} color="green.500" />
                  <Text fontSize="sm" color="gray.600">Live Presentation</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={FaClock} color="orange.500" />
                  <Text fontSize="sm" color="gray.600">Limited Time</Text>
                </HStack>
              </HStack>
            </VStack>
          </VStack>

          {/* Password Form */}
          <Card maxW="md" mx="auto" shadow="xl">
            <CardBody p={8}>
              <form onSubmit={handlePasswordSubmit}>
                <VStack spacing={6}>
                  <FormControl>
                    <FormLabel color="gray.700" fontWeight="medium">Access Password</FormLabel>
                    <Input
                      type="password"
                      placeholder="Enter your access password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      size="lg"
                      disabled={loading}
                      _focus={{ borderColor: '#0066cc', boxShadow: '0 0 0 1px #0066cc' }}
                    />
                  </FormControl>

                  <VStack spacing={4} w="100%">
                    <Button
                      type="submit"
                      bg="#0066cc"
                      color="white"
                      size="lg"
                      w="100%"
                      leftIcon={loading ? <Spinner size="sm" /> : <CheckCircleIcon />}
                      isLoading={loading}
                      loadingText="Verifying..."
                      _hover={{ bg: '#0052a3' }}
                    >
                      Access Presentation
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRequestPassword}
                      color="#0066cc"
                    >
                      Don't have a password? Request access
                    </Button>
                  </VStack>
                </VStack>
              </form>
            </CardBody>
          </Card>

          <Text fontSize="xs" color="gray.500" textAlign="center" maxW="md" mx="auto">
            Your access password was sent to your email. If you can't find it, check your spam folder 
            or request a new password above.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default SecurePitchDeck; 